import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { HorizontalBarList, StatTile, TimeSeriesBarChart, type ChartSeries, type TimePoint } from '@org/ui';
import { useAnalytics } from '../analytics-context';
import { ReportCard } from '../report-card';
import { ReportTable } from '../report-table';
import { SeriesTable } from '../series-table';
import { TimeSeriesCard } from '../time-series-card';
import { formatBucket, formatDateTime, formatRate } from '../format';
import { tileDelta } from '../compare';

export function LearningReports() {
  const { t, i18n } = useTranslation('admin');
  const { input, compareInput, grain, search, rangeDays } = useAnalytics();
  const comparing = Boolean(compareInput);
  const vsLabel =
    search.compare === 'lastYear' ? t('analytics.compare.vsLastYear') : t('analytics.common.vsPrevious', { days: rangeDays });
  const overTime = trpc.analytics.learning.submissionsOverTime.useQuery(input);
  const cmpOverTime = trpc.analytics.learning.submissionsOverTime.useQuery(compareInput ?? input, { enabled: comparing });
  const afterClass = trpc.analytics.learning.homeworkAfterClass.useQuery(input);
  const cmpAfterClass = trpc.analytics.learning.homeworkAfterClass.useQuery(compareInput ?? input, { enabled: comparing });
  const byType = trpc.analytics.learning.submissionsByType.useQuery(input);
  const top = trpc.analytics.learning.topStudents.useQuery(input);
  const backlog = trpc.analytics.learning.gradingBacklog.useQuery(input);
  const conversations = trpc.analytics.learning.conversations.useQuery(input);

  const lang = i18n.language;
  const xLabel = (b: string) => formatBucket(b, grain, lang);
  const overTimeSeries: ChartSeries[] = [
    { key: 'submissions', label: t('analytics.learning.overTime.series.submissions') },
    { key: 'students', label: t('analytics.learning.overTime.series.students') },
  ];
  const convSeries: ChartSeries[] = [
    { key: 'started', label: t('analytics.learning.conversations.series.started') },
    { key: 'completed', label: t('analytics.learning.conversations.series.completed') },
    { key: 'abandoned', label: t('analytics.learning.conversations.series.abandoned') },
  ];
  const typeLabel = (type: string) => t(`analytics.learning.exerciseTypes.${type}`, { defaultValue: type });
  const afterClassSeries: ChartSeries[] = [{ key: 'rate', label: t('analytics.learning.afterClass.series') }];
  const toTrend = (d: typeof afterClass.data): TimePoint[] =>
    (d?.trend ?? []).map((p) => {
      const point: TimePoint = { bucket: p.bucket };
      if (p.rate !== null) point.rate = p.rate;
      return point;
    });
  const afterClassTrend = toTrend(afterClass.data);
  const cmpAfterClassTrend = toTrend(cmpAfterClass.data);

  return (
    <div className="space-y-6">
      <TimeSeriesCard
        title={t('analytics.learning.overTime.title')}
        description={t('analytics.learning.overTime.description')}
        data={overTime.data}
        cmpData={cmpOverTime.data}
        series={overTimeSeries}
        kind="line"
        isLoading={overTime.isLoading}
        error={overTime.error}
      />

      <TimeSeriesCard
        title={t('analytics.learning.afterClass.title')}
        description={t('analytics.learning.afterClass.description')}
        data={afterClassTrend}
        cmpData={comparing ? cmpAfterClassTrend : undefined}
        series={afterClassSeries}
        kind="line"
        yDomain={[0, 100]}
        valueFormat={(n) => formatRate(n)}
        height={200}
        isLoading={afterClass.isLoading}
        error={afterClass.error}
      >
        <div className="mb-4 grid grid-cols-2 gap-3 md:max-w-md">
          <StatTile
            label={t('analytics.learning.afterClass.rate')}
            value={afterClass.data?.overallRate ?? null}
            format="percent"
            delta={tileDelta(afterClass.data?.overallRate, comparing ? cmpAfterClass.data?.overallRate : undefined, vsLabel, { percentPoints: true })}
            hint={afterClass.data ? t('analytics.learning.afterClass.pairs', { done: afterClass.data.done, pairs: afterClass.data.pairs }) : undefined}
          />
          <StatTile label={t('analytics.learning.afterClass.classes')} value={afterClass.data?.byGroup.length ?? null} hint={t('analytics.learning.afterClass.classesHint')} />
        </div>
        <div className="mb-4">
          <HorizontalBarList
            items={(afterClass.data?.byGroup ?? []).slice(0, 12).map((g) => ({
              key: g.groupId,
              label: g.name ?? t('analytics.learning.afterClass.private'),
              value: g.rate ?? 0,
              secondary: `${g.teacherName ?? ''} · ${g.done}/${g.pairs}`,
            }))}
            valueFormat={(n) => formatRate(n)}
            max={100}
          />
        </div>
      </TimeSeriesCard>

      <div className="grid gap-6 lg:grid-cols-2">
        <ReportCard
          title={t('analytics.learning.byType.title')}
          description={t('analytics.learning.byType.description')}
          isLoading={byType.isLoading}
          error={byType.error}
          isEmpty={byType.data?.length === 0}
          table={
            byType.data ? (
              <ReportTable
                rows={byType.data}
                rowKey={(r) => r.exerciseType}
                defaultSort="submissions"
                columns={[
                  { key: 'type', label: t('analytics.common.type'), value: (r) => r.exerciseType, render: (r) => typeLabel(r.exerciseType) },
                  { key: 'submissions', label: t('analytics.learning.byType.submissions'), value: (r) => r.submissions, align: 'right' },
                  { key: 'students', label: t('analytics.learning.byType.students'), value: (r) => r.students, align: 'right' },
                  { key: 'score', label: t('analytics.learning.byType.avgScore'), value: (r) => r.avgScorePct, render: (r) => formatRate(r.avgScorePct), align: 'right' },
                ]}
              />
            ) : null
          }
        >
          <HorizontalBarList
            items={(byType.data ?? []).map((r) => ({
              key: r.exerciseType,
              label: typeLabel(r.exerciseType),
              value: r.submissions,
              secondary:
                r.avgScorePct === null
                  ? t('analytics.learning.byType.manual')
                  : t('analytics.learning.byType.avgScoreInline', { pct: formatRate(r.avgScorePct) }),
            }))}
          />
        </ReportCard>

        <ReportCard
          title={t('analytics.learning.conversations.title')}
          description={t('analytics.learning.conversations.description')}
          isLoading={conversations.isLoading}
          error={conversations.error}
          isEmpty={conversations.data?.totals.started === 0}
          table={
            conversations.data ? <SeriesTable data={conversations.data.series} series={convSeries} grain={grain} /> : null
          }
        >
          <div className="mb-4 grid grid-cols-3 gap-3">
            <StatTile label={t('analytics.learning.conversations.started')} value={conversations.data?.totals.started ?? null} />
            <StatTile
              label={t('analytics.learning.conversations.completionRate')}
              value={conversations.data?.totals.completionRate ?? null}
              format="percent"
            />
            <StatTile
              label={t('analytics.learning.conversations.avgScore')}
              value={conversations.data?.totals.avgScorePct ?? null}
              format="percent"
            />
          </div>
          <TimeSeriesBarChart data={conversations.data?.series ?? []} series={convSeries} stacked height={200} xLabelFormat={xLabel} />
        </ReportCard>
      </div>

      <ReportCard
        title={t('analytics.learning.top.title')}
        description={t('analytics.learning.top.description')}
        isLoading={top.isLoading}
        error={top.error}
        isEmpty={top.data?.length === 0}
      >
        <ReportTable
          rows={top.data ?? []}
          rowKey={(r) => r.userId}
          defaultSort="submissions"
          searchKeys={['name', 'email']}
          columns={[
            { key: 'name', label: t('analytics.common.student'), value: (r) => r.name },
            { key: 'email', label: t('analytics.common.email'), value: (r) => r.email, className: 'text-muted-foreground' },
            { key: 'level', label: t('analytics.common.level'), value: (r) => r.levelName },
            { key: 'submissions', label: t('analytics.learning.top.submissions'), value: (r) => r.submissions, align: 'right' },
            { key: 'exercises', label: t('analytics.learning.top.exercises'), value: (r) => r.exercises, align: 'right' },
            { key: 'allTime', label: t('analytics.learning.top.allTime'), value: (r) => r.allTime, align: 'right' },
            {
              key: 'last',
              label: t('analytics.learning.top.lastSubmission'),
              value: (r) => r.lastSubmissionAt,
              render: (r) => formatDateTime(r.lastSubmissionAt, lang),
            },
          ]}
        />
      </ReportCard>

      <ReportCard
        title={t('analytics.learning.backlog.title')}
        description={t('analytics.learning.backlog.description')}
        isLoading={backlog.isLoading}
        error={backlog.error}
        isEmpty={backlog.data?.total === 0}
      >
        <div className="mb-4 grid grid-cols-2 gap-3 md:max-w-sm">
          <StatTile label={t('analytics.learning.backlog.waiting')} value={backlog.data?.total ?? null} />
          <StatTile label={t('analytics.learning.backlog.olderThan7')} value={backlog.data?.olderThan7Days ?? null} />
        </div>
        <ReportTable
          rows={backlog.data?.items ?? []}
          rowKey={(r) => r.submissionId}
          defaultSort="days"
          searchKeys={['name', 'exercise']}
          columns={[
            { key: 'name', label: t('analytics.common.student'), value: (r) => r.name },
            { key: 'type', label: t('analytics.common.type'), value: (r) => r.exerciseType, render: (r) => typeLabel(r.exerciseType) },
            { key: 'exercise', label: t('analytics.learning.backlog.exercise'), value: (r) => r.exerciseTitle, className: 'max-w-md truncate' },
            {
              key: 'submitted',
              label: t('analytics.learning.backlog.submittedAt'),
              value: (r) => r.submittedAt,
              render: (r) => formatDateTime(r.submittedAt, lang),
            },
            { key: 'days', label: t('analytics.learning.backlog.daysWaiting'), value: (r) => r.daysWaiting, align: 'right' },
          ]}
        />
      </ReportCard>
    </div>
  );
}
